import React from "react"
import _ from "lodash"
import { navigate } from '@reach/router'
// Components
import { WithTip } from 'components/tooltip/with-tip'
import HoverPreview from 'components/table/hovers/hover'
// Data
import carouselService from "data/carousel"
import automationService from "data/automation"
import notificationService from "data/notification"
import { localizeNum, isValidNumber } from "data/lib/numbers"
// Images
import { CarouselIcon } from "images/icons/tools"
import { CaretRightIcon } from "images/icons/general"
// Styles
import { CarouselBtn_s, CarouselIcon_s, CarouselTriggerButtons_s } from './carousel-styles'

type CarouselTriggerProps = {
  controls: {}
  defaults?: {}
  rowData: []
  namedItems?: object
  field: string
  persist: Function
  title: string
  description?: string
  disabled?: boolean
  disabledMessage?: string
  basicReadonly?: (string|{literal: string}|{append: boolean})[]
  basicStates?: string[]
  componentAtRest?: Function
  hover?: {}
  width?: number
  url?: string
  table?: string
}

const hasParentImmutable = (ele: Element, itr: number): boolean => {
  if(itr > 4) return false
  if(ele.parentElement) {
    if(ele.parentElement.classList.contains("systemImmutable-row")) return true
    else return hasParentImmutable(ele.parentElement, itr + 1)
  } else {
    return false
  }
}

const CarouselTrigger = (props: CarouselTriggerProps) => {
  let displayAtRest = ''
  let isDisplayButton = false;
  let hasContent = false;
  const field = _.get(props, ['rowData', props.field])
  const state =  _.get(props, 'rowData.inputtype') || _.get(field, 'state')
  const control = _.get(props, `controls.${state}`)
  const singleControl = _.get(props, `controls.single`);

  if ((state === 'basic' || _.includes(props.basicStates, state)) && props.basicReadonly){
    let chunks: string[] = []
    let append = false
    props.basicReadonly.map((val, i) => {
      let value = ''
      if (typeof val == 'object') {
        if ('literal' in val) {
          value = val.literal
        }
        if ('append' in val) {
          append = true
        }
      } else if (typeof val == 'string') {
        value = _.get(props.rowData, val) || _.get(field, val)
        if (!_.isNil(value)){
          if (isValidNumber(value)){
            value = `${localizeNum(value, 4)}`
          } else {
            value = `${value}`
          }
        } else if (props.rowData[val]){
          value =  `${props.rowData[val]}`
        } else {
          value = '–'
        }
      }
      if (value) {
        if (append) {
          chunks[chunks.length-1] += value
          append = false
        } else {
          chunks.push(value)
        }
      }
    });
    displayAtRest = chunks.join(' | ')
    if (displayAtRest === "") displayAtRest = '–'
  } else if (props.field === 'capdetail' || props.field === 'floordetail'){
    displayAtRest = props.title
    isDisplayButton = true
    if (field && field.type !== 'None') hasContent = true
  } else if (props.componentAtRest) {
    displayAtRest = props.componentAtRest({rowData: props.rowData, field: props.field, namedItems: props.namedItems})
  } else if (control) {
    displayAtRest = control.name
  } else if (props.rowData["systeminput"] === "systemImmutable") {
    displayAtRest = "System"
  } else if (state && state.toLowerCase() === 'none'){
    displayAtRest = '–'
  } else {
    displayAtRest = '–'
  }

  let needsHover:boolean = false
  needsHover = !_.isNil(props.hover) && !_.isNil(state) && _.toLower(state) !== 'none' && _.toLower(state) !== 'inherit'
  if (_.get(props, "hover.injected")) needsHover = true
  if (props.hover && props.field === 'tenantgroupdetail') needsHover = true
  if (props.hover && props.field === 'recoverydetail') needsHover =true
  if (props.hover && props.field === 'recoverypooldetail') needsHover = true
  if (props.hover && props.field === 'users') needsHover = true
  if (props.hover && props.field === 'defaultportfolios') needsHover = true
  if (props.hover && props.field === 'approles') needsHover = true
  if (props.hover && props.field === 'useraccess') needsHover = true

  return (
    <CarouselBtn_s
      className="carousel-button"
      id={`${props.field}-trigger`}
      onClick={e => {
        e.persist()
        if(props.url) navigate(props.url)
        else if (isDisplayButton && !hasParentImmutable(e.target, 0)){
          automationService.broadcast("MODAL_OPENING", e)
          carouselService.updateData({...props, show: true, controls: props.controls})
        } else return
      }}
      onDoubleClick={e => {
        e.persist()
        if(props.url) navigate(props.url)
        else {
          if(hasParentImmutable(e.target, 0)) return // dont open carousel on systemImmutable rows
          else if(props.disabled) {
            let msg = "This carousel is disabled"
            if(props.disabledMessage) msg = props.disabledMessage
            notificationService.notify({
              type: "warn",
              message: msg,
              dismiss: 3000
            })
            return
          }
          automationService.broadcast("MODAL_OPENING", e)
          carouselService.updateData({...props, show: true, controls: props.controls})
        }
      }}
      onKeyDown={e => {
        switch(e.key) {
          case "Enter":
            e.persist()
            if(props.url) navigate(props.url)
            else if(props.disabled) {
              let msg = "This carousel is disabled"
              if(props.disabledMessage) msg = props.disabledMessage
              notificationService.notify({
                type: "warn",
                message: msg,
                dismiss: 3000
              })
              return
            }
            else automationService.broadcast("MODAL_OPENING", e)
            break
        }
      }}
    >
      { !isDisplayButton && singleControl && <CarouselIcon_s src={CaretRightIcon} /> }
      { !isDisplayButton && !singleControl && <CarouselIcon_s src={CarouselIcon} /> }

      { needsHover && isDisplayButton && <WithTip position="bottom left">
        <HoverPreview data={props.rowData} config={props.hover} field={props.field} description={props.description} title={props.title} namedItems={props.namedItems} />
          <CarouselTriggerButtons_s className="cell-trigger-btn" hasContent={hasContent}>{ displayAtRest }</CarouselTriggerButtons_s>
        </WithTip>
      }

      { needsHover && !isDisplayButton && <WithTip position="bottom left">
        <HoverPreview data={props.rowData} config={props.hover} field={props.field} description={props.description} title={props.title} namedItems={props.namedItems} />
        <span>{ displayAtRest }</span>
      </WithTip> }

      { !needsHover && isDisplayButton && <CarouselTriggerButtons_s className="cell-trigger-btn" hasContent={hasContent}>{ displayAtRest }</CarouselTriggerButtons_s > }
      { !needsHover && !isDisplayButton && <span>{ displayAtRest }</span> }

    </CarouselBtn_s>
  )
}

export default CarouselTrigger